// eslint-disable-next-line no-restricted-imports
import { usePathname as useNextPathname } from 'next/navigation'
import { useMemo } from 'react'

import { isExternal, normalizeUrl } from '@/i18n/navigation'
import type { LinkEntry } from '@/shared/contentful/types'
import { getLinkEntryUrl } from '@/shared/contentful/utils'
import { browserEnvs } from '@/shared/envs'
import { getPathPrefix, useLocale } from '@/shared/locale'
import { routes } from '@/shared/routes'

type UseSelectedTopBarItemProps = {
  /**
   * Links to be displayed in the top navigation
   */
  topNavigation: LinkEntry[]
}

export const useSelectedTopBarItem = ({ topNavigation }: UseSelectedTopBarItemProps) => {
  const locale = useLocale()
  const localizedPathname = useNextPathname()

  const selectedTopBarItem = useMemo(() => {
    const pathPrefix = getPathPrefix(browserEnvs.NEXT_PUBLIC_COUNTRY, locale)
    if (!topNavigation) {
      return
    }
    const normalizedPathname = normalizeUrl(localizedPathname.toLowerCase())

    const items = topNavigation
      .filter((item) => item && !Boolean(item.url && isExternal(item.url)))
      .map((item) => {
        const itemUrl = normalizeUrl(
          (getLinkEntryUrl(item, locale, { asString: true }) as string).toLowerCase(),
        )
        return { url: normalizeUrl(`${pathPrefix}${itemUrl}`), name: item.name }
      })

    const matchingItem = items.find((item) => normalizedPathname === item.url)

    if (matchingItem) {
      return matchingItem?.name
    }

    const isHomeUrl = (url: string) => {
      // Check if the URL is the home route or localized home path
      return url === routes.HOME || url === pathPrefix
    }

    const matchingPrefixItem = items.find(
      //Other than home cuz, we expect the home to be always first
      (item) => !isHomeUrl(item.url) && normalizedPathname.startsWith(item.url),
    )

    return matchingPrefixItem ? matchingPrefixItem.name : topNavigation[0]?.name
  }, [topNavigation, localizedPathname, locale])

  return selectedTopBarItem
}
